/**
 * billReceipt — shared receipt-printing helpers for POS and back-office bills.
 * Produces an 80mm thermal receipt PDF via pdfMake using the branch_config
 * flags returned by the API, plus shop_tax (ค่าภาษีของร้าน — เป็นของนิติบุคคล
 * ไม่ใช่ของสาขา ดู silver-spoon-api/docs/vat-compliance.md §2).
 *
 * ชื่อเอกสารบนหัวบิลมี 3 แบบ (ห้ามสลับกัน — ดู documentTitle ด้านล่าง):
 *   ยังไม่ชำระเงิน           → ใบแจ้งรายการ / ใบเรียกเก็บเงิน (ไม่ใช่เอกสารทางภาษี)
 *   ชำระแล้ว + ร้านจด VAT    → ใบเสร็จรับเงิน/ใบกำกับภาษีอย่างย่อ
 *   ชำระแล้ว + ร้านไม่จด VAT → ใบเสร็จรับเงิน (ห้ามมีคำว่าใบกำกับภาษี ห้ามมีบรรทัด VAT)
 *
 * ใบไหนเป็นใบกำกับภาษีอย่างย่อ API เป็นคนตัดสินผ่าน `abbreviated_tax_invoice_no`
 * ห้ามคำนวณเองที่นี่ ดู silver-spoon-api/docs/vat-compliance.md (D1/D2)
 *
 * Expected normalized bill shape (the caller maps API → this shape):
 *   bill_no, payment_status, abbreviated_tax_invoice_no, pos_device_code, delivery_platform,
 *   table_name, customer_name, customer_phone, tax_policy,
 *   subtotal, discount, shipping_price,
 *   service_charge, service_charge_rate,
 *   total_before_vat, vat, vat_rate, grand_total,
 *   items: [{ name, unit_price, quantity, cancelled_quantity, modifiers[],
 *             remark, subtotal, discount, total }]
 *   (subtotal = gross line amount before item discount; total = net)
 */

import QRCode from 'qrcode';
import { generatePromptPayPayload, maskPromptPayId } from '@/utils/promptpay';
import { createReportPdf } from '@/utils/pdfReport';
import { brandName as localizedBrandName } from '@/utils/brand';
import { printerPaperWidth } from '@/utils/printerSettings';

const PROMPTPAY_ICON = require('@/assets/images/icon_promptpay.svg');

function toNum(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

/**
 * รหัสสถานประกอบการบนเอกสารภาษี — `00000` คือสำนักงานใหญ่ ซึ่งบนใบกำกับภาษีไทย
 * เขียนว่า "สำนักงานใหญ่" ไม่ใช่ตัวเลข ส่วนสาขาอื่นเขียนเป็นเลขที่สาขา
 * คืน '' เมื่อยังไม่ได้ตั้งค่า เพื่อให้ผู้เรียกซ่อนทั้งส่วนได้
 */
export function formatBranchNo(branchNo, t = (s) => s) {
  const value = String(branchNo || '').trim();
  if (!value) return '';
  return /^0+$/.test(value) ? t('Head Office') : t('Branch No') + ' ' + value;
}

function formatCancelledQty(qty) {
  const n = toNum(qty);
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

// Rough vertical-height estimate (pt) for one pdfmake content node. Used only to
// decide whether a receipt is "short" enough to need bottom padding so the page
// stays portrait — it does NOT need to be exact (the page still uses height:'auto',
// which grows to the real content). Covers the node shapes this file builds:
// text / columns / image / canvas.
function estimateNodeHeight(node) {
  if (!node) return 0;
  const m = Array.isArray(node.margin) ? node.margin : [0, 0, 0, 0];
  const marginV = (m[1] || 0) + (m[3] || 0);
  let h = 0;
  if (node.image) {
    h = node.width || 50;                 // QR/logo ≈ square → height ≈ width
  } else if (node.canvas) {
    h = 1;                                // thin divider line
  } else if (Array.isArray(node.columns)) {
    const fs = Math.max(...node.columns.map(c => c.fontSize || 8));
    h = fs * 1.5;
  } else if (typeof node.text === 'string') {
    h = node.text ? (node.fontSize || 8) * 1.5 : 0; // empty spacer = margin only
  }
  return h + marginV;
}

// Group identical items together and subtract cancelled quantities.
export function groupItemsForReceipt(items) {
  const groups = new Map();
  (items || []).forEach(item => {
    if (item.cancelled) return;
    const activeQty = Math.max(0, toNum(item.quantity) - toNum(item.cancelled_quantity));
    if (activeQty <= 0) return;
    const modKey = (item.modifiers || []).map(m => m.id || m.name).join('|');
    const key = `${item.product_id || item.name}::${item.unit_price}::${modKey}`;
    const existing = groups.get(key);
    if (!existing) {
      groups.set(key, {
        name: item.name,
        unit_price: toNum(item.unit_price),
        quantity: activeQty,
        // Gross line amount (before item discount). Falls back to `total` for
        // callers that don't supply a separate subtotal.
        subtotal: toNum(item.subtotal) || toNum(item.total),
        discount: toNum(item.discount),
        total: toNum(item.total),
        is_reward: !!item.is_reward, // ของแถมจากการแลกแต้ม
        modifiers: (item.modifiers || []).map(m => ({ ...m })),
        remark: item.remark || ''
      });
    } else {
      existing.quantity += activeQty;
      existing.subtotal += toNum(item.subtotal) || toNum(item.total);
      existing.discount += toNum(item.discount);
      existing.total += toNum(item.total);
    }
  });
  return Array.from(groups.values()).map(g => ({
    ...g,
    subtotal: +g.subtotal.toFixed(2),
    discount: +g.discount.toFixed(2),
    total: +g.total.toFixed(2)
  }));
}

// Convert any remote URL into a data URL for pdfMake (which can't fetch http).
export async function toDataUrl(url) {
  if (!url) return '';
  if (url.startsWith('data:')) return url;
  try {
    const resp = await fetch(url, { mode: 'cors', credentials: 'omit' });
    if (!resp.ok) throw new Error('HTTP ' + resp.status);
    const blob = await resp.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.warn('Failed to load image for receipt:', url, e);
    return '';
  }
}

// PromptPay QR + brand-icon overlay. Returns data URL or '' on failure.
export async function buildPromptPayQrImage(method, amount) {
  if (!method || !method.promptpay_no) return '';
  const merchantName = method.account_name || '';
  const payload = generatePromptPayPayload(method.promptpay_no, amount, merchantName, '');
  const qrDataUrl = await QRCode.toDataURL(payload, { errorCorrectionLevel: 'H', margin: 1, width: 600 });

  const loadImage = (src) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

  try {
    const qrImg = await loadImage(qrDataUrl);
    const size = qrImg.width;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(qrImg, 0, 0, size, size);
    try {
      const iconImg = await loadImage(PROMPTPAY_ICON);
      const logoSize = Math.round(size * 0.18);
      const cx = (size - logoSize) / 2;
      const cy = (size - logoSize) / 2;
      ctx.drawImage(iconImg, cx, cy, logoSize, logoSize);
    } catch (e) {
      console.warn('PromptPay icon overlay failed:', e);
    }
    return canvas.toDataURL('image/png');
  } catch (e) {
    return qrDataUrl;
  }
}

export async function buildReviewQrImage(reviewUrl) {
  if (!reviewUrl) return '';
  return await QRCode.toDataURL(reviewUrl, { errorCorrectionLevel: 'M', margin: 1, width: 600 });
}

/**
 * Build the pdfMake doc definition for a single bill receipt.
 *
 * @param {object} opts
 * @param {object} opts.bill             normalized bill shape
 * @param {object} opts.configuration    branch_config (show_* flags + branch fields)
 * @param {object} opts.shopTax          filter.shop_tax (tax_policy, vat_rate, tax_id, tax_entity_name)
 * @param {string} opts.promptpayQrDataUrl
 * @param {string} opts.reviewQrDataUrl
 * @param {object} opts.promptpayMethod  the branch_payment_method with promptpay_no
 * @param {string} opts.brandName        title at the top of the receipt
 * @param {function} opts.fmt            currency formatter
 * @param {function} opts.t              i18n translator (default identity)
 * @param {string} opts.sampleNotice     ข้อความกำกับท้ายใบว่าเป็นใบตัวอย่าง ('' = ใบจริง)
 */
export function buildReceiptDocDefinition({
  bill,
  configuration,
  shopTax = {},
  promptpayQrDataUrl = '',
  reviewQrDataUrl = '',
  promptpayMethod = null,
  /**
   * ใบตัวอย่างสำหรับแนบคำขอ ภ.พ.06 — ตั้งข้อความนี้แล้วจะมีบรรทัดกำกับที่ท้ายใบ
   *
   * ตั้งใจให้เป็นบรรทัดเล็กท้ายใบ ไม่ใช่ลายน้ำทับหัวใบ เพราะเจ้าหน้าที่ต้องเห็น
   * "รูปแบบใบกำกับภาษีอย่างย่อ" ครบทุกองค์ประกอบตามที่กฎหมายกำหนด แต่ตัวกระดาษ
   * ก็ต้องประกาศตัวเองว่าไม่ใช่เอกสารทางภาษีจริง (กัน ม.86/13)
   */
  sampleNotice = '',
  // Header brand title — single source via env so each deployment sets its own
  // (callers no longer pass this). Falls back to the original brand if unset.
  // ตามภาษาที่เลือกอยู่ (name/name_en) — ไม่ได้ตั้งชื่ออังกฤษไว้ก็ตกกลับไปใช้ชื่อไทยเอง
  brandName = localizedBrandName(),
  fmt,
  t = (s) => s,
  // The short-page bottom padding only matters for the PDF/driver path (keeps it
  // portrait). For WebUSB raster we cut exactly under the content, so callers pass
  // false to drop the trailing blank.
  padShortPage = true
}) {
  const cfg = configuration || {};
  // ภาษีอยู่ระดับร้าน — สาขาเหลือแค่ branch_no (รหัสสถานประกอบการ)
  const tax = shopTax || {};

  // Paper-width profile. 72mm/80mm use the on-device-verified numbers (see
  // thermal-printer-setup.md). 58mm is a real narrower relayout — same font
  // sizes (kept readable), a narrower page so long item names wrap, with the
  // page width, side margins, separators, QR images and the right-hand amount
  // columns all refit to the ~130pt content width. The 58mm page width keeps the
  // same 203dpi ratio as 72mm (136.06pt → 384 dots) so the WebUSB raster prints
  // 1:1. NOTE: 58mm values are not yet on-device verified.
  //
  // promptpayQr 100pt (≈35mm) is sized off the real payload: a PromptPay QR with
  // an amount is only 89 chars → version 6 = 41×41 modules, so 100pt still gives
  // ~6.6 dots (0.8mm) per module at 203dpi — comfortably above what a phone needs,
  // while the old 140pt burned ~14mm of paper on every bill for nothing. The 58mm
  // profile keeps 92pt: it is already near the floor (~0.75mm/module) and unverified
  // on-device, so it is not worth shaving.
  const is58mm = String(cfg.bill_paper_width || '') === '58mm';
  const P = is58mm
    ? { pageWidth: 136.06, sideMargin: 2.83, sepWidth: 130, minBody: 155, logo: 40, promptpayQr: 92, reviewQr: 80, amtSm: 40, amtMd: 48, amtLg: 56 }
    : { pageWidth: 204.09, sideMargin: 4.25, sepWidth: 195, minBody: 230, logo: 50, promptpayQr: 100, reviewQr: 120, amtSm: 50, amtMd: 60, amtLg: 70 };

  const items = groupItemsForReceipt(bill.items);
  /**
   * นโยบายภาษีของ "ใบนี้" = snapshot บนบิล (เขียนตอนปิดการขาย) ไม่ใช่ค่าปัจจุบันของร้าน
   * ร้านจด VAT ทีหลัง/เลิกจด แล้วพิมพ์ใบเก่าซ้ำ ต้องได้หน้าตาเหมือนวันที่ขายเสมอ
   * — ฝั่ง API (TaxInvoiceHelper::abbreviatedNoOf) ก็อ่าน snapshot บนบิลเหมือนกัน
   *
   * InfoResource ส่งมาเป็น Str::headline() = "No Tax" ส่วน PrintResource ส่งค่าดิบ "no_tax"
   * จึง normalize ก่อนเทียบ · บิลเก่าที่ยังไม่มี snapshot ค่อยถอยไปใช้ค่าปัจจุบันของร้าน
   */
  const billTaxPolicy = String(bill.tax_policy || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  const isNoTax = billTaxPolicy
    ? billTaxPolicy === 'no_tax'
    : String(tax.tax_policy || '').toLowerCase() === 'no_tax';

  // API เขียนค่านี้ตอนปิดการขายของร้านที่จด VAT — ใบนี้คือใบกำกับภาษีอย่างย่อ
  // ใช้คุมหัวเอกสาร บรรทัดเลขที่ และบรรทัด VAT ที่บังคับต้องมี
  //
  // ออกใบกำกับเต็มรูปให้ลูกค้าทีหลัง **ไม่ทำให้ค่านี้หายไป** — ใบย่อคือแม่บท
  // ส่วนใบเต็มรูปเป็นเอกสาร "ออกแทน" (docs/vat-compliance.md D11)
  const abbrTaxInvoiceNo = String(bill.abbreviated_tax_invoice_no || '');
  const isAbbrTaxInvoice = !isNoTax && abbrTaxInvoiceNo !== '';

  /**
   * บิลที่ยังไม่ได้รับเงิน = **ใบแจ้งรายการ ไม่ใช่เอกสารทางภาษี**
   * (เช็คบิลก่อนจ่าย / กดพิมพ์บิลที่ยังค้างจ่าย)
   *
   * ต้องเปลี่ยนหน้าตาให้ชัด ไม่งั้นลูกค้าถือกระดาษที่หน้าตาเหมือนใบกำกับภาษีออกไป
   * ทั้งที่ร้านยังไม่ได้บันทึกภาษีขาย — หัวบิล/เลขเอกสารเปลี่ยน · ซ่อนรหัสเครื่อง POS
   * และข้อความ "ราคารวม VAT" (ทั้งคู่อยู่ใต้ isAbbrTaxInvoice อยู่แล้ว) · เติมข้อความปฏิเสธท้ายบิล
   */
  const PAID_STATUSES = ['paid', 'platform_pending', 'partially_refunded', 'refunded'];
  const isCheckBill = !PAID_STATUSES.includes(String(bill.payment_status || ''));

  /**
   * รับเงินแล้ว = ต้องออก "ใบรับ" ตามมาตรา 105 ประมวลรัษฎากร ไม่ว่าร้านจะจด VAT หรือไม่
   * ใบรับต้องมีครบ: ชื่อผู้ประกอบการ · ที่อยู่ · เลขประจำตัวผู้เสียภาษี · วันที่ · เลขที่รันอัตโนมัติ
   * ทั้งหมดนี้จึงพิมพ์เสมอบนใบที่ชำระแล้ว ไม่ขึ้นกับ toggle หน้าตั้งค่าใบเสร็จ
   * (toggle ยังมีผลกับใบแจ้งรายการ ซึ่งไม่ใช่เอกสารทางภาษี)
   */
  const isPaidReceipt = !isCheckBill;

  /**
   * 58mm วาง label กับค่ายาวๆ ในบรรทัดเดียวไม่พอ — เลขใบกำกับ 23 ตัวจะถูกบีบจนอ่านไม่ออก
   * ค่าที่ยาวเกินจึงตกลงบรรทัดถัดไปชิดขวาแทน (80mm ยังเป็น 2 คอลัมน์เหมือนเดิม)
   */
  const metaRow = (label, value) => {
    const text = String(value ?? '');
    if (is58mm && text.length > 14) {
      return [
        { text: label, fontSize: 9 },
        { text, fontSize: 9, alignment: 'right', margin: [0, 0, 0, 1] },
      ];
    }
    return [{
      columns: [
        { text: label, fontSize: 9 },
        { text, fontSize: 9, alignment: 'right' }
      ],
      margin: [0, 0, 0, 1]
    }];
  };

  const itemsContent = [];
  // Type scale for an item block: the name/amount line is what the customer
  // actually reads, so it sits at 8 — level with the totals, which stay ahead of
  // it on weight (bold) rather than size. Everything hanging off it (qty x price,
  // options, note, line discount) stays a step down at 7. Before this the name was
  // 7 and the qty line 6, i.e. the menu items were the smallest print on a receipt
  // whose meta rows run at 9 — backwards.
  items.forEach(item => {
    itemsContent.push({
      columns: [
        { text: t(item.name) + (item.is_reward ? ' (' + t('Reward') + ')' : ''), fontSize: 8 },
        // Gross line amount; the per-item discount (if any) is shown below and
        // aggregated into the totals' Discount line so the receipt reconciles.
        { text: item.subtotal ? fmt(item.subtotal) : (item.is_reward ? t('Free') : ''), fontSize: 8, alignment: 'right', width: P.amtSm }
      ],
      margin: [0, 2, 0, 2]
    });
    if (item.quantity > 1) {
      itemsContent.push({
        columns: [
          { text: `${formatCancelledQty(item.quantity)} X ${fmt(item.unit_price)}`, fontSize: 7 }
        ],
        margin: [0, 0, 0, 1]
      });
    }
    if (item.modifiers && item.modifiers.length > 0) {
      item.modifiers.forEach(m => {
        // The item price above is already all-in (base + options), so options are
        // NOT a separate amount column. Instead annotate the add-on price inline
        // after the name ("- เพิ่มลูกชิ้น +10") so it reads as a note, not a line to
        // be summed. Free options show the name only. Plain black — thermal heads
        // are 1-bit; hierarchy comes from the smaller italic face.
        const addOn = toNum(m.price);
        itemsContent.push({
          text: `- ${t(m.name)}${addOn > 0 ? ' +' + fmt(addOn) : ''}`,
          fontSize: 7,
          italics: true,
          margin: [3, 0, 0, 1]
        });
      });
    }
    if (cfg.show_item_note && item.remark) {
      itemsContent.push({
        text: `  * ${item.remark}`,
        fontSize: 7,
        italics: true,
        margin: [0, 0, 0, 1]
      });
    }
    if (item.discount > 0) {
      itemsContent.push({
        columns: [
          { text: `  ${t('Discount')}`, fontSize: 7, italics: true },
          { text: '-' + fmt(item.discount), fontSize: 7, italics: true, alignment: 'right', width: P.amtMd }
        ],
        margin: [0, 0, 0, 1]
      });
    }
  });

  const totalsContent = [];
  // `bill.subtotal` is gross (before item discounts). Fold per-item discounts
  // into the displayed Subtotal so item discounts appear once (per line) and the
  // Discount line below stays scoped to the bill-level discount only.
  const itemDiscountTotal = items.reduce((s, i) => s + toNum(i.discount), 0);
  const netSubtotal = +(toNum(bill.subtotal) - itemDiscountTotal).toFixed(2);
  if (!isNoTax) {
    totalsContent.push({
      columns: [
        { text: t('Subtotal'), fontSize: 8, bold: true },
        { text: fmt(netSubtotal), fontSize: 8, bold: true, alignment: 'right', width: P.amtMd }
      ],
      margin: [0, 0, 0, 2]
    });
  }
  if (bill.discount > 0) {
    totalsContent.push({
      columns: [
        { text: t('Discount'), fontSize: 8 },
        { text: '-' + fmt(bill.discount), fontSize: 8, alignment: 'right', width: P.amtMd }
      ],
      margin: [0, 0, 0, 2]
    });
  }
  if (bill.shipping_price > 0) {
    totalsContent.push({
      columns: [
        { text: t('Shipping'), fontSize: 8 },
        { text: fmt(bill.shipping_price), fontSize: 8, alignment: 'right', width: P.amtMd }
      ],
      margin: [0, 0, 0, 2]
    });
  }
  // Service charge is not a tax matter — it is already added into the total, so the
  // line explaining it must always print, VAT-registered or not. (It used to sit behind
  // the branch's show_tax_breakdown toggle *and* a !isNoTax guard, which meant a non-VAT
  // shop charging service could never show it. Toggle dropped — see the migration
  // 2026_09_09_180000 and vat-compliance.md §2.)
  if (bill.service_charge > 0) {
    totalsContent.push({
      columns: [
        { text: `${t('Service Charge')} (${bill.service_charge_rate}%)`, fontSize: 8 },
        { text: fmt(bill.service_charge), fontSize: 8, alignment: 'right', width: P.amtMd }
      ],
      margin: [0, 0, 0, 2]
    });
  }
  // VAT breakdown prints whenever the bill actually carries VAT. That is exactly the set
  // of bills where it is mandatory (an abbreviated tax invoice must show the tax amount)
  // and it is meaningless anywhere else — a no_tax shop has no VAT to break down.
  if (!isNoTax && bill.vat > 0) {
    if (bill.total_before_vat > 0) {
      totalsContent.push({
        columns: [
          { text: t('Amount before VAT'), fontSize: 8 },
          { text: fmt(bill.total_before_vat), fontSize: 8, alignment: 'right', width: P.amtMd }
        ],
        margin: [0, 0, 0, 2]
      });
    }
    totalsContent.push({
      columns: [
        { text: `${t('VAT')} (${bill.vat_rate}%)`, fontSize: 8 },
        { text: fmt(bill.vat), fontSize: 8, alignment: 'right', width: P.amtMd }
      ],
      margin: [0, 0, 0, 2]
    });
  }

  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  // เวลาที่กดพิมพ์ — ใช้เป็นบรรทัดปิดท้ายเท่านั้น ไม่ใช่วันที่ของเอกสารภาษี
  const thaiDate = `${now.getDate()}/${now.getMonth() + 1}/${now.getFullYear() + 543} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;

  // วันที่บนใบกำกับภาษี = วันที่ปิดการขาย (พิมพ์ซ้ำทีหลังต้องได้วันเดิม)
  const issuedAt = bill.closed_at ? new Date(bill.closed_at) : now;
  const issuedDate = isNaN(issuedAt.getTime()) ? now : issuedAt;
  const taxInvoiceDate = `${issuedDate.getDate()}/${issuedDate.getMonth() + 1}/${issuedDate.getFullYear() + 543}`
    + ` ${pad(issuedDate.getHours())}:${pad(issuedDate.getMinutes())}`;

  // Header
  const headerContent = [];
  if (cfg.show_logo && cfg.logo_url) {
    headerContent.push({ image: cfg.logo_url, width: P.logo, alignment: 'center', margin: [0, 0, 0, 6] });
  }
  // ใบรับ/ใบกำกับภาษีต้องแสดง "ชื่อผู้ประกอบการตามทะเบียน" ไม่ใช่ชื่อแบรนด์
  // (แบรนด์ "ครัวคุณย่า" นิติบุคคลอาจเป็น "บจก. ก อาหาร") — ตั้งที่หน้าตั้งค่าร้าน
  // ร้านไม่จด VAT ก็ต้องใช้ชื่อผู้ประกอบการจริงบนบิลเงินสดเหมือนกัน
  const sellerName = (isPaidReceipt && tax.tax_entity_name) ? tax.tax_entity_name : brandName;
  if (sellerName) {
    headerContent.push({ text: t(sellerName), alignment: 'center', fontSize: 14, bold: true });
  }
  // ยังโชว์ชื่อแบรนด์ไว้ด้วยเมื่อชื่อตามทะเบียนไม่เหมือนกัน — ลูกค้าจำร้านจากแบรนด์
  if (isPaidReceipt && tax.tax_entity_name && brandName && tax.tax_entity_name !== brandName) {
    headerContent.push({ text: t(brandName), alignment: 'center', fontSize: 9, margin: [0, 1, 0, 0] });
  }
  if (cfg.show_branch_name && cfg.name) {
    headerContent.push({ text: t('Branch') + ': ' + cfg.name, alignment: 'center', fontSize: 8, margin: [0, 2, 0, 0] });
  }
  // ที่อยู่ผู้ประกอบการ — บังคับบนใบรับทุกแบบ (ทั้งใบเสร็จรับเงินและใบกำกับภาษีอย่างย่อ)
  // toggle ตั้งไว้ default = false จึงห้ามให้มันปิดที่อยู่บนเอกสารทางภาษีได้
  if (cfg.show_branch_address || isPaidReceipt) {
    const addrParts = [cfg.address, cfg.sub_district, cfg.district, cfg.province, cfg.postal_code].filter(Boolean);
    if (addrParts.length) {
      headerContent.push({ text: addrParts.join(' '), alignment: 'center', fontSize: 9, margin: [0, 1, 0, 0] });
    }
  }
  if (cfg.show_branch_phone) {
    const phones = [cfg.telephone1, cfg.telephone2].filter(Boolean).join(' / ');
    if (phones) {
      headerContent.push({ text: t('Tel.') + ' ' + phones, alignment: 'center', fontSize: 9, margin: [0, 1, 0, 0] });
    }
  }
  // เลขประจำตัวผู้เสียภาษีของผู้ขาย — บังคับบนใบรับทุกแบบ ไม่ใช่เฉพาะใบกำกับภาษี
  // ร้านไม่จด VAT ใช้เลขบัตรประชาชน/เลขทะเบียนนิติบุคคล (ตั้งที่หน้าตั้งค่าร้าน)
  // toggle เหลือผลเฉพาะใบแจ้งรายการที่ยังไม่ชำระเงิน
  if ((isPaidReceipt || cfg.show_tax_id) && tax.tax_id) {
    // รหัสสถานประกอบการ (สนญ./สาขาที่ …) เป็นเรื่องของทะเบียน VAT
    // ร้านไม่จด VAT ไม่มีรหัสนี้ ใส่ไปจะกลายเป็นอ้างสถานะที่ไม่มีจริง
    const branchLabel = isNoTax ? '' : formatBranchNo(cfg.branch_no, t);
    const branchSuffix = branchLabel ? ` (${branchLabel})` : '';
    headerContent.push({
      text: t('Tax ID') + ': ' + tax.tax_id + branchSuffix,
      alignment: 'center',
      fontSize: 8,
      margin: [0, 1, 0, 0]
    });
  }
  if (cfg.show_receipt_header && cfg.receipt_header) {
    headerContent.push({ text: cfg.receipt_header, alignment: 'center', fontSize: 9, margin: [0, 6, 0, 0] });
  }
  /**
   * ชื่อเอกสารบนหัวบิล — มี 3 แบบเท่านั้น และห้ามสลับกันเด็ดขาด
   *
   *   ยังไม่ชำระเงิน           → ใบแจ้งรายการ / ใบเรียกเก็บเงิน (ไม่ใช่เอกสารทางภาษี)
   *   ชำระแล้ว + ร้านจด VAT    → ใบเสร็จรับเงิน/ใบกำกับภาษีอย่างย่อ (ใบเดียวทำสองหน้าที่)
   *   ชำระแล้ว + ร้านไม่จด VAT → ใบเสร็จรับเงิน
   *
   * ร้านไม่จด VAT ห้ามมีคำว่า "ใบกำกับภาษี" บนใบเสร็จเด็ดขาด — เป็นการออกใบกำกับภาษี
   * โดยไม่มีสิทธิ์ (มาตรา 86/13) โทษหนักทั้งเบี้ยปรับและอาญา
   *
   * ก่อนหน้านี้ใบที่ชำระแล้วของร้านไม่จด VAT ไม่มีหัวเอกสารเลย ซึ่งใช้เป็นใบรับไม่ได้
   */
  const documentTitle = isCheckBill
    ? t('Bill Check')
    : (isAbbrTaxInvoice ? t('Receipt/Abbreviated Tax Invoice') : t('Receipt'));
  headerContent.push({
    text: documentTitle,
    alignment: 'center',
    fontSize: 10,
    bold: true,
    margin: [0, 6, 0, 0]
  });
  headerContent.push({ text: '', margin: [0, 0, 0, 10] });

  // Metadata
  const metaContent = [];
  if (isAbbrTaxInvoice) {
    // The bill number IS the abbreviated tax invoice number (D1), so it is printed
    // regardless of the branch's show_bill_number preference — a tax invoice without
    // a number is not a tax invoice.
    metaContent.push(...metaRow(t('Invoice No.'), abbrTaxInvoiceNo));
    // วันที่ออกใบกำกับ = วันที่ปิดการขาย ไม่ใช่เวลาที่กดพิมพ์
    // (พิมพ์ซ้ำเดือนหน้าต้องยังเป็นวันที่ขายเดิม ไม่งั้นเอกสารภาษีผิดงวด)
    metaContent.push(...metaRow(t('Date'), taxInvoiceDate));
    // เลขประจำเครื่องที่สรรพากรออกให้ (ภ.พ.06) — พิมพ์บรรทัดนี้เสมอบนใบกำกับอย่างย่อ
    // ร้านที่ยังไม่ได้ยื่นก็ยังขายได้ ปล่อยค่าว่างไว้ (vat-compliance.md D10)
    //
    // ใช้ 'POS' ตรงๆ ไม่ผ่าน t() — เป็นคำเดียวกันทุกภาษา และกระดาษ 58mm แคบ
    // ป้าย "รหัสเครื่อง POS" ยาวเกินไปจนเบียดเลขที่อยู่ขวามือ
    metaContent.push(...metaRow('POS', bill.pos_device_code || ''));
  } else if (isPaidReceipt) {
    /**
     * ใบเสร็จรับเงิน/บิลเงินสด (ร้านไม่จด VAT หรือบิลเก่าที่ไม่มีเลขใบกำกับ)
     * ใบรับต้องมี "เลขที่" ที่รันอัตโนมัติและ "วันที่" เสมอ ไม่ขึ้นกับ toggle
     * ใช้ bill_no เป็นเลขที่ใบเสร็จ — รันต่อเนื่องรายวันต่อสาขาอยู่แล้ว (BillHelper::generateBillNo)
     */
    if (bill.bill_no) {
      metaContent.push(...metaRow(t('Receipt No.'), bill.bill_no));
    }
    // วันที่บนใบรับ = วันที่รับเงิน/ปิดการขาย ไม่ใช่เวลาที่กดพิมพ์
    metaContent.push(...metaRow(t('Date'), taxInvoiceDate));
  } else if (bill.bill_no) {
    // ใบแจ้งรายการต้องมีเลขอ้างอิงเสมอ ไม่ขึ้นกับ toggle — ลูกค้าใช้อ้างตอนมาจ่าย
    metaContent.push(...metaRow(t('Bill No.'), bill.bill_no));
  }
  /**
   * ออเดอร์ delivery platform — ต้องบอกช่องทางขายและช่องทางรับเงินบนใบ
   * เพื่อให้บัญชีตัดยอดสิ้นวันได้ว่าก้อนนี้เก็บกับแพลตฟอร์ม ไม่ใช่เงินในลิ้นชัก
   * (ยอดบนใบเป็นราคาเต็มบนแอป ไม่หัก GP — GP เป็นค่าใช้จ่ายคนละก้อน)
   */
  if (bill.delivery_platform) {
    metaContent.push({
      columns: [
        { text: t('Channel'), fontSize: 9 },
        { text: String(bill.delivery_platform).toUpperCase(), fontSize: 9, alignment: 'right' }
      ],
      margin: [0, 0, 0, 1]
    });
    const platformPayment = (bill.payments || []).find(p => p && p.payment_method);
    metaContent.push({
      columns: [
        { text: t('Payment'), fontSize: 9 },
        {
          text: platformPayment ? t(platformPayment.payment_method) : String(bill.delivery_platform),
          fontSize: 9,
          alignment: 'right'
        }
      ],
      margin: [0, 0, 0, 1]
    });
  }
  if (cfg.show_table_number && bill.table_name) {
    metaContent.push({
      columns: [{ text: t('Table'), fontSize: 9 }, { text: bill.table_name, fontSize: 9, alignment: 'right' }],
      margin: [0, 0, 0, 1]
    });
  }
  if (cfg.show_customer_info && (bill.customer_name || bill.customer_phone)) {
    const info = [bill.customer_name, bill.customer_phone].filter(Boolean).join(' / ');
    metaContent.push({
      columns: [{ text: t('Customer'), fontSize: 9 }, { text: info, fontSize: 9, alignment: 'right' }],
      margin: [0, 0, 0, 1]
    });
  }
  if (metaContent.length) metaContent.push({ text: '', margin: [0, 4, 0, 0] });

  // QR
  const qrSection = [];
  if (cfg.show_qrcode_promptpay && promptpayMethod && promptpayMethod.promptpay_no && promptpayQrDataUrl) {
    qrSection.push(
      { text: t('Scan to pay'), alignment: 'center', fontSize: 8, bold: true, margin: [0, 8, 0, 2] },
      { text: 'PromptPay', alignment: 'center', fontSize: 8, margin: [0, 0, 0, 6] },
      { image: promptpayQrDataUrl, width: P.promptpayQr, alignment: 'center' },
      { text: 'PromptPay: ' + maskPromptPayId(promptpayMethod.promptpay_no), alignment: 'center', fontSize: 9, margin: [0, 6, 0, 0] }
    );
    if (promptpayMethod.account_name) {
      qrSection.push({ text: promptpayMethod.account_name, alignment: 'center', fontSize: 9 });
    }
    // `_Bill.Amount` = "จำนวนเงิน" — คีย์กลาง `Amount` แปลว่า "จำนวน" ซึ่งอ่านเป็นจำนวนนับ
    qrSection.push({ text: `${t('_Bill.Amount')} ${fmt(bill.grand_total)}`, alignment: 'center', fontSize: 9, bold: true, margin: [0, 2, 0, 0] });
  }
  if (cfg.show_qrcode_review && cfg.review_url && reviewQrDataUrl) {
    qrSection.push(
      { text: t('Review us'), alignment: 'center', fontSize: 8, bold: true, margin: [0, 8, 0, 2] },
      { image: reviewQrDataUrl, width: P.reviewQr, alignment: 'center', margin: [0, 4, 0, 0] }
    );
  }

  // Loyalty points — server-computed; printed only when the bill carries them.
  const loyaltySection = [];
  const loyalty = bill.loyalty;
  if (loyalty && (loyalty.earned_points != null || loyalty.balance_after != null)) {
    loyaltySection.push({ canvas: [{ type: 'line', x1: 0, y1: 0, x2: P.sepWidth, y2: 0, dash: { length: 1 }, lineWidth: 0.5, lineColor: '#999' }], margin: [0, 6, 0, 4] });
    if (loyalty.earned_points != null) {
      loyaltySection.push({
        columns: [
          { text: t('Points Earned'), fontSize: 9 },
          { text: '+' + toNum(loyalty.earned_points).toLocaleString(), fontSize: 9, alignment: 'right', width: P.amtLg }
        ],
        margin: [0, 0, 0, 1]
      });
    }
    if (loyalty.balance_after != null) {
      loyaltySection.push({
        columns: [
          { text: t('Points Balance'), fontSize: 9 },
          { text: toNum(loyalty.balance_after).toLocaleString(), fontSize: 9, alignment: 'right', width: P.amtLg }
        ],
        margin: [0, 0, 0, 1]
      });
    }
  }

  const trailing = [];
  // ใบตัวอย่างสำหรับยื่น ภ.พ.06 — ประกาศตัวก่อนใคร จะได้ไม่มีใครเอาไปใช้เป็นใบจริง
  if (sampleNotice) {
    trailing.push({
      text: sampleNotice,
      alignment: 'center',
      fontSize: 8,
      bold: true,
      margin: [0, 8, 0, 0]
    });
  }
  // กันเข้าใจผิดว่าเป็นใบเสร็จ/ใบกำกับ — ต้องอยู่ก่อนข้อความท้ายบิลของร้าน จะได้เห็นชัด
  if (isCheckBill) {
    trailing.push({
      text: '* ' + t('This document is not a receipt or a tax invoice.'),
      alignment: 'center',
      fontSize: 8,
      margin: [0, 8, 0, 0]
    });
  }
  if (cfg.show_receipt_footer && cfg.receipt_footer) {
    trailing.push({ text: cfg.receipt_footer, alignment: 'center', fontSize: 8, margin: [0, 8, 0, 4] });
  }
  trailing.push({ text: thaiDate, alignment: 'center', fontSize: 6, margin: [0, 8, 0, 0] });

  /**
   * บรรทัดผู้ให้บริการระบบ — อยู่ล่างสุดจริงๆ ใต้เวลาที่พิมพ์ ตัวเล็กที่สุดบนใบ
   * ตั้งใจให้ไม่ไปแย่งพื้นที่กับข้อมูลที่กฎหมายบังคับ (หัวใบ/เลขที่/VAT)
   *
   * อ่านจาก runtime config → ร้านที่ไม่อยากให้มี ตั้ง WEB_POWERED_BY="" ที่ shops/<slug>/.env
   * แล้ว restart ได้เลย ไม่ต้อง build ใหม่
   */
  const poweredBy = process.env.VUE_APP_POWERED_BY;
  if (poweredBy) {
    trailing.push({
      text: 'Powered by ' + poweredBy,
      alignment: 'center',
      fontSize: 6,
      color: '#666',
      margin: [0, 2, 0, 0]
    });
  }

  // 80mm thermal paper, 72mm printable — 1 mm = 2.83465 pt
  //   page = 72 mm → 204.09 pt, matching the driver's printable width so jobs
  //   print 1:1 (an 80mm page gets "fit to page" scaled down ~10% and blurs);
  //   side margins = 1.5 mm, on top of the printer's ~4mm hardware margins
  const content = [
    ...headerContent,
    ...metaContent,
    ...itemsContent,
    { text: '', margin: [0, 6, 0, 0] },
    ...totalsContent,
    {
      columns: [
        { text: t('Grand Total'), fontSize: 9, bold: true },
        { text: fmt(bill.grand_total), fontSize: 9, bold: true, alignment: 'right', width: P.amtLg }
      ],
      margin: [0, 6, 0, 0]
    },
    // ข้อกำหนดของใบกำกับภาษีอย่างย่อ: ต้องระบุชัดว่าราคาที่แสดงรวม VAT แล้ว
    // ใช้ได้ทั้ง included และ excluded เพราะยอดบนใบเสร็จเป็นราคารวม VAT ทั้งคู่
    // (SaleOrderHelper::calculatePrices บวก VAT เข้า price ตั้งแต่ระดับ item ในโหมด excluded)
    ...(isAbbrTaxInvoice ? [{
      text: t('Prices shown include VAT'),
      fontSize: 8,
      alignment: 'center',
      margin: [0, 4, 0, 0]
    }] : []),
    ...loyaltySection,
    // QR ต้องอยู่เหนือบล็อกปิดท้าย — เดิม QR ถูกวางไว้ล่างสุด ทำให้ "เวลาที่พิมพ์"
    // กับบรรทัด Powered by ไปแทรกกลางใบ ทั้งที่ทั้งคู่ต้องเป็นบรรทัดสุดท้ายของกระดาษ
    // (พรีวิวหน้าตั้งค่าใบเสร็จเรียงถูกมาตั้งแต่ต้น — ตอนนี้ใบจริงตรงกับพรีวิวแล้ว)
    ...qrSection,
    ...trailing
  ];

  // Keep the page portrait. With height:'auto', a short order produces a page
  // shorter than its 72mm (204.09pt) width; EPSON's driver then treats it as
  // landscape and feeds a long blank header. Pad the body so the content always
  // exceeds the page width → page stays taller than wide. Tall receipts already
  // clear MIN_BODY_HEIGHT, so no padding is added and they print exactly as before.
  const MIN_BODY_HEIGHT = P.minBody; // pt; comfortably > page width so the page stays portrait
  const estimated = content.reduce((sum, n) => sum + estimateNodeHeight(n), 0);
  if (padShortPage && estimated < MIN_BODY_HEIGHT) {
    // Reserve the height with a zero-width white canvas line, NOT a bottom margin:
    // pdfmake collapses the trailing element's margin when computing height:'auto',
    // so a margin-only spacer is ignored and the page stays short (landscape). A
    // drawn canvas height is always counted.
    content.push({
      canvas: [{ type: 'line', x1: 0, y1: 0, x2: 0, y2: MIN_BODY_HEIGHT - estimated, lineWidth: 0.001, lineColor: '#ffffff' }]
    });
  }

  return {
    pageSize: { width: P.pageWidth, height: 'auto' },
    // Bottom margin: the PDF/driver path keeps 8.5pt for a tidy page, but on the
    // WebUSB path (padShortPage false) it is blank paper before an exact cut, so
    // trim it to a hair under the last line.
    pageMargins: [P.sideMargin, 8.5, P.sideMargin, padShortPage ? 8.5 : 2],
    defaultStyle: { font: 'Sarabun', fontSize: 8 },
    content
  };
}

// Pre-process branch_config: convert remote logo to data URL when show_logo is on
//
// bill_paper_width is overwritten with THIS device's paper width: receipt content
// is the branch's, but the paper is whatever printer is attached here
// (utils/printerSettings.js). Every receipt path (layout profile below +
// dotsForPaperWidth at the call sites) reads it from here, so one line covers them.
export async function preparePrintConfig(branchConfig) {
  const cfg = { ...(branchConfig || {}), bill_paper_width: printerPaperWidth() };
  if (cfg.show_logo) {
    if (cfg.logo_base64) {
      cfg.logo_url = cfg.logo_base64;
    } else if (cfg.logo_url) {
      cfg.logo_url = await toDataUrl(cfg.logo_url);
    }
  }
  return cfg;
}

/**
 * Render PDF + send to printer.
 *   - Desktop: hidden iframe → browser print dialog
 *   - Mobile (iOS/Android): opens PDF in popup (caller must pre-open inside
 *     a user gesture so Safari doesn't block)
 */
export async function printPdf(docDef, { popup = null } = {}) {
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const pdf = createReportPdf(docDef);
  const blob = await pdf.getBlob();
  const url = URL.createObjectURL(blob);

  if (isMobile) {
    if (popup) popup.location.href = url;
    else window.location.href = url;
    setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
    return;
  }

  let iframe = document.getElementById('__bill_print_frame');
  if (iframe) iframe.remove();
  iframe = document.createElement('iframe');
  iframe.id = '__bill_print_frame';
  iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:0;height:0;border:none;';
  iframe.src = url;
  document.body.appendChild(iframe);
  iframe.onload = () => {
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
}
